import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { vendorRequestQueries, settingsQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans, card, th, td, topBtn, linkBtn } from '../Vendors/VendorDetails';
import PhoneNumberInput from '../../components/PhoneNumberInput';
import { LinkedInLink } from '../../components/LinkedInField';
import { buildPreOnboardingReport, newReportId, PreOnboardingReportData, ReportLogo } from '../Reports/reportPdf';

/** The FluxWatch logo for the PDF header, downscaled once so the file stays small (same as the Reports page). */
const loadReportLogo = () => new Promise<ReportLogo | null>(resolve => {
  const img = new Image();
  img.onload = () => {
    try {
      const w = Math.min(560, img.naturalWidth), h = Math.round(w * img.naturalHeight / img.naturalWidth);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve({ data: canvas.toDataURL('image/png'), w, h });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = '/logo.png';
});

type Status = 'pending' | 'approved' | 'rejected' | 'needs_info' | 'promoted';
type Tier = 'Critical' | 'High' | 'Medium' | 'Low';
type View = 'review' | 'ready' | 'onboarded' | 'rejected' | 'all';
type SortKey = 'newest' | 'oldest' | 'tier' | 'score';

interface ScanSummary {
  scan_id: string;
  status: 'processing' | 'completed' | 'failed' | 'unavailable';
  overall_score?: number;
  risk_level?: string;
  completed_at?: string;
}

interface VendorRequest {
  id: string;
  vendor_name: string;
  domain: string;
  reason: string | null;
  data_sensitivity: string;
  estimated_spend: string | null;
  risk_tier: Tier;
  status: Status;
  reviewer_notes: string | null;
  decided_at: string | null;
  created_at: string;
  created_vendor_id: string | null;
  requested_by_name: string | null;
  scan: ScanSummary | null;
  ai_research_summary: string | null;
  ai_research_sources: { url: string; label: string }[] | null;
  ai_suggested_data_sensitivity: string | null;
  ai_research_rationale: string | null;
  ai_research_generated_at: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  linkedin_url: string | null;
  linkedin_source: 'website' | 'manual' | null;
  linkedin_found_on: string | null;
  insight_summary: string | null;
  insight_top_findings: { name: string; category: string | null; severity: string; score_gain_if_fixed?: number }[] | null;
  insight_generated_at: string | null;
}

interface RequestDocument {
  id: string;
  title: string;
  source_url: string;
  source_type: string;
  document_type: string | null;
  extraction_status: string | null;
  created_at: string;
}

// Requested -> Screened -> Reviewed -> Onboarded, derived from the request's own fields.
const PIPELINE_STAGES = ['Requested', 'Screened', 'Reviewed', 'Onboarded'];
export function pipelineStage(r: Pick<VendorRequest, 'status' | 'scan'>): number {
  if (r.status === 'promoted') return 3;
  if (r.status === 'approved' || r.status === 'rejected' || r.status === 'needs_info') return 2;
  if (r.scan && r.scan.status === 'completed') return 1;
  return 0;
}

export function VendorLogo({ name, domain, size = 32, isDark }: { name: string; domain: string; size?: number; isDark: boolean }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || '?').trim().charAt(0).toUpperCase();
  const boxStyle: React.CSSProperties = {
    width: size, height: size, borderRadius: size >= 40 ? 10 : 7, flexShrink: 0,
    background: isDark ? '#1a1a1a' : '#eef2ff',
  };
  if (failed || !domain) {
    return (
      <div style={{
        ...boxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: isDark ? '#94a3b8' : '#4338CA', fontWeight: 700, fontSize: size * 0.4,
      }}>{initials}</div>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      onError={() => setFailed(true)}
      alt=""
      style={{ ...boxStyle, objectFit: 'contain' }}
    />
  );
}

export function PipelineStepper({ stage, size = 'sm', isDark = false }: { stage: number; size?: 'sm' | 'lg'; isDark?: boolean }) {
  const dot = size === 'lg' ? 11 : 8;
  const gap = size === 'lg' ? 18 : 15;
  const lg = size === 'lg';
  const reached = isDark ? '#e2e8f0' : T.ink;
  const unreached = isDark ? '#333' : '#E2E8F0';
  const unreachedLabel = isDark ? '#64748b' : '#94A3B8';
  return (
    <div style={{ display: 'flex', alignItems: lg ? 'flex-start' : 'center', width: 'fit-content' }}>
      {PIPELINE_STAGES.map((label, i) => (
        <div key={label} style={{ display: 'flex', alignItems: lg ? 'flex-start' : 'center', flexShrink: 0 }}>
          {lg ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 68 }}>
              <div style={{ width: dot, height: dot, borderRadius: '50%', flexShrink: 0, background: i <= stage ? reached : unreached }} />
              <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 5, textAlign: 'center', lineHeight: 1.3, color: i <= stage ? reached : unreachedLabel }}>{label}</div>
            </div>
          ) : (
            <div title={label} style={{ width: dot, height: dot, borderRadius: '50%', background: i <= stage ? reached : unreached, flexShrink: 0 }} />
          )}
          {i < PIPELINE_STAGES.length - 1 && (
            <div style={{ width: gap, height: 2, flexShrink: 0, marginTop: lg ? 5 : 0, background: i < stage ? reached : unreached }} />
          )}
        </div>
      ))}
    </div>
  );
}

const SENSITIVITY: Record<string, string> = {
  none: 'No sensitive data',
  internal: 'Internal data only',
  pii: 'Customer PII',
  financial: 'Financial or payment data',
  regulated: 'Regulated (health, financial, etc.)',
};
// Mirrors services/risk_tiering.py so the form can preview the tier the backend will assign.
const TIER_BY_SENSITIVITY: Record<string, Tier> = { none: 'Low', internal: 'Medium', pii: 'High', financial: 'High', regulated: 'Critical' };
const TIER_RANK: Record<Tier, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
const TIER_COLOR: Record<Tier, string> = { Critical: '#B91C1C', High: '#EA580C', Medium: '#D97706', Low: '#15803D' };

const STATUS: Record<Status, { label: string; color: string }> = {
  pending: { label: 'Pending review', color: '#D97706' },
  needs_info: { label: 'Needs info', color: '#7C3AED' },
  approved: { label: 'Approved', color: '#2563EB' },
  promoted: { label: 'Onboarded', color: '#15803D' },
  rejected: { label: 'Rejected', color: '#64748B' },
};

const STALE_WAIT_DAYS = 7;
const APP_HEADER_HEIGHT = 64;
const PANEL_SLIDE_MS = 220;
const POLL_MS = 5000;

const DAY_MS = 86_400_000;
const daysSince = (iso: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)) : null);
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const waitLabel = (days: number) => (days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`);
const apiError = (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d: any) => String(d.msg || '').replace(/^Value error, /, '')).join(' ') || fallback;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') return detail.message;
  return fallback;
};
const inReview = (r: VendorRequest) => r.status === 'pending' || r.status === 'needs_info';
const EMPTY: never[] = [];
const REQUEST_DATA: string[][] = [['vendor-requests']];
const LIST_KEY = vendorRequestQueries.list().queryKey;

// Show the server's answer immediately; the invalidation that follows confirms it from the source of truth.
const patchRequest = (client: QueryClient, id: string, patch: (r: VendorRequest) => VendorRequest | null) =>
  client.setQueryData(LIST_KEY, (old: VendorRequest[] | undefined) =>
    old?.flatMap(r => (r.id === id ? (patch(r) ?? []) : [r])));
const normalizeDomain = (value: string) =>
  value.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, '').split(/[/?#]/)[0].split('@').pop()!.split(':')[0].replace(/\.$/, '');

function Pill({ label, color, isDark }: { label: string; color: string; isDark: boolean }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
      color, background: `${color}${isDark ? '26' : '14'}`, border: `1px solid ${color}${isDark ? '55' : '33'}`,
    }}>{label}</span>
  );
}

function ScreeningCell({ scan, isDark }: { scan: ScanSummary | null; isDark: boolean }) {
  const muted = isDark ? '#94a3b8' : T.muted;
  if (!scan) return <span style={{ color: muted }}>Not screened</span>;
  if (scan.status === 'processing') return <span style={{ color: muted }}>Scanning…</span>;
  if (scan.status === 'failed') return <span style={{ color: isDark ? '#F87171' : T.red }}>Scan failed</span>;
  if (scan.status === 'unavailable') return <span style={{ color: muted }}>No longer available</span>;
  const tier = (scan.risk_level || 'Low') as Tier;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: TIER_COLOR[tier] || (isDark ? '#e2e8f0' : T.ink) }}>{scan.overall_score}</span>
      <span style={{ fontSize: 11, color: muted }}>{scan.risk_level}</span>
    </span>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────── */

export default function VendorRequests() {
  const navigate = useNavigate();
  const { requestId: linkedId } = useParams<{ requestId?: string }>();
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const canAct = !!user && user.role !== 'viewer';
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const ink = isDark ? '#e2e8f0' : T.ink;
  const muted = isDark ? '#94a3b8' : T.muted;
  const slate = isDark ? '#94a3b8' : T.slate;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const bg = isDark ? '#000' : T.bg;
  const danger = isDark ? '#F87171' : T.red;
  const amber = isDark ? '#FBBF24' : T.amb;

  const queryClient = useQueryClient();
  const requestsQuery = useQuery({
    ...vendorRequestQueries.list(),
    // A running screening scan is checked every few seconds until it finishes.
    refetchInterval: query => ((query.state.data ?? []) as VendorRequest[]).some(r => r.scan?.status === 'processing') ? POLL_MS : false,
  });
  const requests: VendorRequest[] = requestsQuery.data ?? EMPTY;
  const loading = requestsQuery.isPending;
  const loadError = requestsQuery.isError ? apiError(requestsQuery.error, 'Could not load requests. Try again.') : '';

  const scanMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/v1/vendor-requests/${id}/scan`)).data as { scan_id: string },
    onSuccess: (data, id) => patchRequest(queryClient, id, r => ({ ...r, scan: { scan_id: data.scan_id, status: 'processing' } })),
    meta: { invalidates: REQUEST_DATA },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/vendor-requests/${id}`),
    onSuccess: (_data, id) => patchRequest(queryClient, id, () => null),
    meta: { invalidates: REQUEST_DATA },
  });
  const [view, setView] = useState<View | null>(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<Tier | ''>('');
  const [sort, setSort] = useState<SortKey>('oldest');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [panelId, setPanelId] = useState<string | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const panelTimer = useRef<number | undefined>(undefined);
  const [deleting, setDeleting] = useState<VendorRequest | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState<{ message: string; tone: 'ok' | 'error' } | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const showToast = (message: string, tone: 'ok' | 'error' = 'ok') => {
    window.clearTimeout(toastTimer.current);
    setToast({ message, tone });
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  };

  const openPanel = (id: string) => {
    window.clearTimeout(panelTimer.current);
    setPanelId(id);
    setPanelVisible(true);
  };
  const closePanel = () => {
    setPanelVisible(false);
    window.clearTimeout(panelTimer.current);
    panelTimer.current = window.setTimeout(() => setPanelId(null), PANEL_SLIDE_MS);
    if (linkedId) navigate('/vendor-requests', { replace: true });
  };

  // Open on "Needs review" when anything is waiting, the first time the list arrives.
  useEffect(() => {
    if (requestsQuery.data && view === null) setView(requestsQuery.data.some(inReview) ? 'review' : 'all');
  }, [requestsQuery.data, view]);
  useEffect(() => () => { window.clearTimeout(panelTimer.current); window.clearTimeout(toastTimer.current); }, []);

  // /vendor-requests/:id opens that request's drawer -- on first load and on in-app navigation alike,
  // since moving between the two routes keeps this page mounted.
  const requestsLoaded = requests.length > 0;
  useEffect(() => {
    if (linkedId && requests.some(r => r.id === linkedId)) openPanel(linkedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId, requestsLoaded]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || creating || bulkUploading) return;
      if (deleting) { if (!busy) setDeleting(null); }
      else if (panelId) closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creating, bulkUploading, deleting, busy, panelId]);

  /* ─── Derived ─── */

  const review = requests.filter(inReview);
  const ready = requests.filter(r => r.status === 'approved');
  const onboarded = requests.filter(r => r.status === 'promoted');
  const rejected = requests.filter(r => r.status === 'rejected');
  const oldestWait = review.reduce((max, r) => Math.max(max, daysSince(r.created_at) ?? 0), 0);
  const onboardedRecently = onboarded.filter(r => (daysSince(r.decided_at || r.created_at) ?? Infinity) <= 30).length;
  const unscannedInReview = review.filter(r => !r.scan || r.scan.status === 'failed' || r.scan.status === 'unavailable').length;

  const currentView: View = view ?? 'all';
  const views: { key: View; label: string; count: number; alert?: boolean }[] = [
    { key: 'review', label: 'Needs review', count: review.length },
    { key: 'ready', label: 'Ready to onboard', count: ready.length, alert: ready.length > 0 },
    { key: 'onboarded', label: 'Onboarded', count: onboarded.length },
    { key: 'rejected', label: 'Rejected', count: rejected.length },
    { key: 'all', label: 'All', count: requests.length },
  ];
  const base = currentView === 'review' ? review : currentView === 'ready' ? ready
    : currentView === 'onboarded' ? onboarded : currentView === 'rejected' ? rejected : requests;

  const query = search.trim().toLowerCase();
  const filtered = base.filter(r =>
    (!query || [r.vendor_name, r.domain, r.requested_by_name, r.reason].some(f => f?.toLowerCase().includes(query))) &&
    (!tierFilter || r.risk_tier === tierFilter)
  );
  const sorted = [...filtered].sort((a, b) => {
    switch (sort) {
      case 'newest': return b.created_at.localeCompare(a.created_at);
      case 'oldest': return a.created_at.localeCompare(b.created_at);
      case 'tier': return TIER_RANK[a.risk_tier] - TIER_RANK[b.risk_tier] || a.created_at.localeCompare(b.created_at);
      case 'score': {
        const sa = a.scan?.status === 'completed' ? a.scan.overall_score ?? 101 : 101;
        const sb = b.scan?.status === 'completed' ? b.scan.overall_score ?? 101 : 101;
        return sa - sb;
      }
      default: return 0;
    }
  });

  const panelRequest = requests.find(r => r.id === panelId) || null;
  const selectable = (r: VendorRequest) => inReview(r) && r.scan?.status !== 'processing';
  const selectedRequests = requests.filter(r => selected.has(r.id) && selectable(r));
  const filtersActive = !!(query || tierFilter);

  const changeView = (next: View) => { setView(next); setSelected(new Set()); };

  /* ─── Actions ─── */

  const runScan = (r: VendorRequest) => scanMutation.mutateAsync(r.id);

  const runBulkScan = async () => {
    setBusy(true);
    const results = await Promise.allSettled(selectedRequests.map(runScan));
    setBusy(false);
    setSelected(new Set());
    const started = results.filter(x => x.status === 'fulfilled').length;
    const failed = results.length - started;
    showToast(failed ? `Started ${started} screening scan${started === 1 ? '' : 's'}; ${failed} could not start.`
                     : `Started ${started} screening scan${started === 1 ? '' : 's'}.`, failed && !started ? 'error' : 'ok');
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    setDeleteError('');
    try {
      if (panelId === deleting.id) closePanel();
      await deleteMutation.mutateAsync(deleting.id);
      showToast(`Request for ${deleting.vendor_name} deleted.`);
      setDeleting(null);
    } catch (error) {
      setDeleteError(apiError(error, 'Could not delete the request.'));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '7px 10px', fontSize: 12.5, fontFamily: sans, color: ink, background: surface,
    border: `1px solid ${line}`, borderRadius: 6, outline: 'none', boxSizing: 'border-box',
  };

  if (loading) {
    return <div style={{ fontFamily: sans, background: bg, minHeight: '100vh', padding: 40, textAlign: 'center', color: muted, fontSize: 13 }}>Loading requests…</div>;
  }

  const kpis = [
    { label: 'Needs review', value: review.length, color: review.length ? amber : ink,
      sub: review.length ? `oldest waiting ${waitLabel(oldestWait)}` : 'nothing waiting', to: 'review' as View },
    { label: 'Not screened', value: unscannedInReview, color: unscannedInReview ? amber : ink,
      sub: 'in review without a screening scan', to: 'review' as View },
    { label: 'Ready to onboard', value: ready.length, color: ready.length ? '#2563EB' : ink,
      sub: 'approved, not yet added as vendors', to: 'ready' as View },
    { label: 'Onboarded', value: onboarded.length, color: ink,
      sub: `${onboardedRecently} in the last 30 days`, to: 'onboarded' as View },
  ];

  return (
    <div style={{ fontFamily: sans, background: bg, minHeight: '100vh' }}>
      <div style={{
        padding: '16px 24px', borderBottom: `1px solid ${line}`, background: surface,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: ink, margin: 0 }}>Pre-onboarding</h1>
          <p style={{ fontSize: 12, color: muted, margin: '2px 0 0' }}>Screen and approve a prospective vendor before it becomes a tracked, scored vendor.</p>
        </div>
        {canAct && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={topBtn(false, isDark)} onClick={() => setBulkUploading(true)}>Bulk upload</button>
            <button style={topBtn(true, isDark)} onClick={() => setCreating(true)}>+ New request</button>
          </div>
        )}
      </div>

      <div style={{ padding: '20px 24px', display: 'grid', gap: 16 }}>
        {loadError && (
          <div style={{ ...card(isDark), color: danger, fontSize: 12.5 }}>
            {loadError} <button style={linkBtn(isDark)} onClick={() => requestsQuery.refetch()}>Retry</button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {kpis.map(k => (
            <button key={k.label} onClick={() => changeView(k.to)} style={{ ...card(isDark), textAlign: 'left', cursor: 'pointer', fontFamily: sans, padding: '14px 16px' }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: k.color, marginTop: 6, lineHeight: 1 }}>{k.value}</div>
              <div style={{ fontSize: 11.5, color: muted, marginTop: 6, lineHeight: 1.35 }}>{k.sub}</div>
            </button>
          ))}
        </div>

        <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
          <div role="tablist" style={{ display: 'flex', gap: 4, padding: '10px 12px', borderBottom: `1px solid ${line}`, overflowX: 'auto', scrollbarWidth: 'thin' }}>
            {views.map(v => {
              const active = currentView === v.key;
              return (
                <button key={v.key} role="tab" aria-selected={active} onClick={() => changeView(v.key)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 99, border: 'none',
                  cursor: 'pointer', fontFamily: sans, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                  background: active ? (isDark ? '#e2e8f0' : T.ink) : 'transparent', color: active ? (isDark ? '#0d0d0d' : '#fff') : slate,
                }}>
                  {v.label}
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '0 6px', borderRadius: 99, lineHeight: '17px',
                    background: v.alert ? '#DC2626' : active ? '#ffffff22' : surface2, color: v.alert ? '#fff' : 'inherit',
                  }}>{v.count}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: `1px solid ${line}`, flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search vendor, domain, requester…"
              aria-label="Search requests" style={{ ...inputStyle, width: 240, maxWidth: '100%' }} />
            <select aria-label="Inherent tier" value={tierFilter} onChange={e => setTierFilter(e.target.value as Tier | '')} style={inputStyle}>
              <option value="">All tiers</option>
              {(Object.keys(TIER_RANK) as Tier[]).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select aria-label="Sort" value={sort} onChange={e => setSort(e.target.value as SortKey)} style={inputStyle}>
              <option value="oldest">Oldest first</option>
              <option value="newest">Newest first</option>
              <option value="tier">Highest tier first</option>
              <option value="score">Lowest screening score first</option>
            </select>
            {filtersActive && <button style={linkBtn(isDark)} onClick={() => { setSearch(''); setTierFilter(''); }}>Clear filters</button>}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: muted }}>{sorted.length} {sorted.length === 1 ? 'request' : 'requests'}</span>
          </div>

          {selected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: `1px solid ${line}`,
              background: isDark ? '#0a1628' : T.blueBg, fontSize: 12.5, color: ink, flexWrap: 'wrap',
            }}>
              <strong>{selectedRequests.length} selected</strong>
              <button style={linkBtn(isDark)} onClick={runBulkScan} disabled={busy || !selectedRequests.length}>
                {busy ? 'Starting scans…' : 'Run screening scan'}
              </button>
              <button style={{ ...linkBtn(isDark), color: muted }} onClick={() => setSelected(new Set())}>Clear selection</button>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            {sorted.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead>
                  <tr>
                    {canAct && (
                      <th style={{ ...th(isDark), width: 34 }}>
                        <input type="checkbox" aria-label="Select all requests in review"
                          checked={sorted.some(selectable) && sorted.filter(selectable).every(r => selected.has(r.id))}
                          disabled={!sorted.some(selectable)}
                          onChange={e => setSelected(e.target.checked ? new Set(sorted.filter(selectable).map(r => r.id)) : new Set())} />
                      </th>
                    )}
                    <th style={th(isDark)}>Vendor</th>
                    <th style={th(isDark)}>Inherent tier</th>
                    <th style={th(isDark)}>Screening</th>
                    <th style={th(isDark)}>Status</th>
                    <th style={th(isDark)}>Requested</th>
                    <th style={th(isDark)}>Waiting</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(r => {
                    const open = panelId === r.id;
                    const wait = inReview(r) ? daysSince(r.created_at) : null;
                    return (
                      <tr key={r.id} data-request-row onClick={() => (open ? closePanel() : openPanel(r.id))}
                        style={{ cursor: 'pointer', background: open ? (isDark ? '#0f1e38' : T.blueBg) : 'transparent' }}
                        onMouseEnter={e => { if (!open) e.currentTarget.style.background = surface2; }}
                        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'transparent'; }}>
                        {canAct && (
                          <td style={td(isDark)} onClick={e => e.stopPropagation()}>
                            <input type="checkbox" aria-label={`Select ${r.vendor_name}`} disabled={!selectable(r)}
                              checked={selected.has(r.id)}
                              onChange={e => setSelected(prev => { const next = new Set(prev); if (e.target.checked) next.add(r.id); else next.delete(r.id); return next; })} />
                          </td>
                        )}
                        <td style={td(isDark)}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 200 }}>
                            <VendorLogo name={r.vendor_name} domain={r.domain} size={30} isDark={isDark} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.vendor_name}</div>
                              <div style={{ fontSize: 11, color: muted }}>{r.domain}</div>
                            </div>
                          </div>
                        </td>
                        <td style={td(isDark)}>
                          <Pill label={r.risk_tier} color={TIER_COLOR[r.risk_tier]} isDark={isDark} />
                          <div style={{ fontSize: 11, color: muted, marginTop: 3 }}>{SENSITIVITY[r.data_sensitivity] || r.data_sensitivity}</div>
                        </td>
                        <td style={td(isDark)}><ScreeningCell scan={r.scan} isDark={isDark} /></td>
                        <td style={td(isDark)}><Pill label={STATUS[r.status].label} color={STATUS[r.status].color} isDark={isDark} /></td>
                        <td style={{ ...td(isDark), color: muted, whiteSpace: 'nowrap' }}>
                          <div style={{ color: ink }}>{fmtDate(r.created_at)}</div>
                          {r.requested_by_name && <div style={{ fontSize: 11 }}>by {r.requested_by_name}</div>}
                        </td>
                        <td style={{ ...td(isDark), whiteSpace: 'nowrap', color: wait === null ? muted : wait > STALE_WAIT_DAYS ? amber : ink }}>
                          {wait === null ? '—' : waitLabel(wait)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {sorted.length === 0 && (
              <div style={{ textAlign: 'center', padding: '44px 16px' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: ink }}>
                  {filtersActive ? 'No requests match these filters'
                    : requests.length === 0 ? 'No requests yet'
                    : currentView === 'review' ? 'Nothing waiting for review'
                    : currentView === 'ready' ? 'No approved requests waiting to be onboarded'
                    : currentView === 'onboarded' ? 'Nothing onboarded through pre-onboarding yet'
                    : 'No rejected requests'}
                </div>
                <div style={{ fontSize: 12.5, color: muted, marginTop: 4 }}>
                  {requests.length === 0 && canAct ? 'Start a request before onboarding a new vendor, or bulk upload a list.' : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {panelRequest && (
        <RequestPanel
          request={panelRequest} isDark={isDark} canAct={canAct} canDelete={isAdmin} visible={panelVisible}
          onClose={closePanel}
          onGoToOnboarding={() => navigate('/vendors', { state: { activeTab: 'onboarding-requests' } })}
          onDelete={r => { setDeleteError(''); setDeleting(r); }}
        />
      )}

      {deleting && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !busy) setDeleting(null); }}>
          <div role="dialog" aria-modal="true" aria-label="Delete request" style={{ width: '100%', maxWidth: 420, background: surface, border: `1px solid ${line}`, borderRadius: 10, padding: 20, fontFamily: sans }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 8 }}>Delete request</div>
            <div style={{ fontSize: 13, color: muted, lineHeight: 1.55 }}>
              Delete the request for <strong style={{ color: ink }}>{deleting.vendor_name}</strong> ({deleting.domain})? Its screening scan stays in scan history. This can't be undone.
            </div>
            {deleteError && <div role="alert" style={{ fontSize: 12, color: danger, marginTop: 10 }}>{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button style={topBtn(false, isDark)} onClick={() => setDeleting(null)} disabled={busy}>Cancel</button>
              <button style={{ ...topBtn(true, isDark), background: '#DC2626', opacity: busy ? 0.7 : 1 }} onClick={confirmDelete} disabled={busy}>
                {busy ? 'Deleting…' : 'Delete request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <NewRequestModal
          isDark={isDark}
          onClose={() => setCreating(false)}
          onCreated={(req, scanError) => {
            setCreating(false);
            queryClient.setQueryData(LIST_KEY, (old: VendorRequest[] | undefined) => [req, ...(old ?? []).filter(r => r.id !== req.id)]);
            queryClient.invalidateQueries({ queryKey: ['vendor-requests'] });
            changeView('review');
            openPanel(req.id);
            if (scanError) showToast(scanError, 'error');
          }}
        />
      )}

      {bulkUploading && (
        <BulkUploadModal
          isDark={isDark}
          onClose={() => setBulkUploading(false)}
          onDone={created => {
            setBulkUploading(false);
            queryClient.invalidateQueries({ queryKey: ['vendor-requests'] });
            if (created) { changeView('review'); showToast(`${created} request${created === 1 ? '' : 's'} added. Select them to run screening scans in bulk.`); }
          }}
        />
      )}

      {toast && (
        <div role="status" style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 1400, maxWidth: 380, display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', borderRadius: 8, fontSize: 12.5, fontFamily: sans, background: surface, color: ink,
          border: `1px solid ${toast.tone === 'error' ? danger : line}`,
        }}>
          <span style={{ color: toast.tone === 'error' ? danger : T.grn, fontWeight: 700 }}>{toast.tone === 'error' ? '!' : '✓'}</span>
          <span style={{ flex: 1 }}>{toast.message}</span>
          <button aria-label="Dismiss" onClick={() => setToast(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, fontSize: 14 }}>×</button>
        </div>
      )}
    </div>
  );
}

/* ─── Review drawer ────────────────────────────────────────────────── */

function RequestPanel({ request: r, isDark, canAct, canDelete, visible, onClose, onGoToOnboarding, onDelete }: {
  request: VendorRequest; isDark: boolean; canAct: boolean; canDelete: boolean; visible: boolean;
  onClose: () => void; onGoToOnboarding: () => void; onDelete: (r: VendorRequest) => void;
}) {
  const queryClient = useQueryClient();
  const scanMutation = useMutation({
    mutationFn: async () => (await api.post(`/v1/vendor-requests/${r.id}/scan`)).data as { scan_id: string },
    onSuccess: data => patchRequest(queryClient, r.id, req => ({ ...req, scan: { scan_id: data.scan_id, status: 'processing' } })),
    meta: { invalidates: REQUEST_DATA },
  });
  const decisionMutation = useMutation({
    mutationFn: async (status: 'approved' | 'rejected' | 'needs_info') =>
      (await api.post(`/v1/vendor-requests/${r.id}/decision`, { status, notes: notes.trim() || null })).data as VendorRequest,
    onSuccess: updated => patchRequest(queryClient, r.id, () => updated),
    meta: { invalidates: REQUEST_DATA },
  });
  const sensitivityMutation = useMutation({
    mutationFn: async (data_sensitivity: string) =>
      (await api.patch(`/v1/vendor-requests/${r.id}/data-sensitivity`, { data_sensitivity })).data as VendorRequest,
    onSuccess: updated => patchRequest(queryClient, r.id, () => updated),
    meta: { invalidates: REQUEST_DATA },
  });
  const ink = isDark ? '#e2e8f0' : T.ink;
  const muted = isDark ? '#94a3b8' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const surface = isDark ? '#0d0d0d' : T.surface;
  const surface2 = isDark ? '#141414' : T.surface2;
  const danger = isDark ? '#F87171' : T.red;
  const amber = isDark ? '#FBBF24' : T.amb;

  const panelRef = useRef<HTMLElement>(null);
  const [notes, setNotes] = useState(r.reviewer_notes || '');
  const [error, setError] = useState('');
  const scanBusy = scanMutation.isPending;
  const deciding = decisionMutation.isPending ? decisionMutation.variables : null;

  useEffect(() => { setNotes(r.reviewer_notes || ''); setError(''); }, [r.id, r.reviewer_notes]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (panelRef.current?.contains(target) || target.closest('[data-request-row], [role="dialog"]')) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const reviewing = inReview(r);
  const wait = daysSince(r.created_at);

  // Pre-onboarding summary PDF: built in the browser from one read, same engine as every other report.
  const { user, tenant } = useAuth() as any;
  const [exporting, setExporting] = useState(false);
  const exportSummary = async () => {
    setExporting(true);
    setError('');
    try {
      const [{ data }, logo] = await Promise.all([
        api.get(`/v1/vendor-requests/${r.id}/summary-report`),
        loadReportLogo(),
      ]);
      const doc = buildPreOnboardingReport(data as PreOnboardingReportData, {
        reportId: newReportId('PRE'), generatedAt: new Date(),
        preparedFor: tenant?.company_name || user?.company || 'Your organization',
        preparedBy: user?.name || user?.email || 'FluxWatch', classification: 'Confidential', logo,
      });
      doc.save(`pre-onboarding-${r.domain}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      setError(apiError(err, 'Could not build the summary. Try again.'));
    } finally {
      setExporting(false);
    }
  };

  const runScan = () => {
    setError('');
    scanMutation.mutate(undefined, { onError: err => setError(apiError(err, 'Could not start the screening scan.')) });
  };

  const decide = (status: 'approved' | 'rejected' | 'needs_info') => {
    setError('');
    decisionMutation.mutate(status, { onError: err => setError(apiError(err, 'Could not save the decision.')) });
  };

  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${surface2}` }}>
      <span style={{ color: muted }}>{label}</span>
      <span style={{ color: value ? ink : muted, textAlign: 'right' }}>{value || '—'}</span>
    </div>
  );
  const sectionTitle = (text: string) => <div style={{ fontSize: 12, fontWeight: 700, color: ink, marginBottom: 6 }}>{text}</div>;
  const scan = r.scan;

  return (
    <aside ref={panelRef} data-request-panel role="dialog" aria-label={`${r.vendor_name} request`} style={{
      position: 'fixed', top: APP_HEADER_HEIGHT, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 1150, background: surface,
      borderLeft: `1px solid ${line}`, display: 'flex', flexDirection: 'column', fontFamily: sans,
      animation: `${visible ? 'requestPanelIn' : 'requestPanelOut'} ${PANEL_SLIDE_MS}ms ${visible ? 'ease-out' : 'ease-in'} forwards`,
    }}>
      <style>{`
        @keyframes requestPanelIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes requestPanelOut { from { transform: translateX(0); } to { transform: translateX(100%); } }
        @media (prefers-reduced-motion: reduce) { aside[data-request-panel] { animation-duration: 1ms !important; } }
      `}</style>

      <div style={{ padding: '16px 18px', borderBottom: `1px solid ${line}` }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <VendorLogo name={r.vendor_name} domain={r.domain} size={40} isDark={isDark} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink }}>{r.vendor_name}</div>
            <a href={`https://${r.domain}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: isDark ? '#60A5FA' : T.blue, textDecoration: 'none' }}>{r.domain} ↗</a>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <Pill label={STATUS[r.status].label} color={STATUS[r.status].color} isDark={isDark} />
              <Pill label={`${r.risk_tier} tier`} color={TIER_COLOR[r.risk_tier]} isDark={isDark} />
              <button onClick={exportSummary} disabled={exporting} title="Download the due-diligence summary of this request as a PDF"
                style={{ ...topBtn(false, isDark), padding: '3px 10px', fontSize: 11.5, marginLeft: 'auto', opacity: exporting ? 0.7 : 1 }}>
                {exporting ? 'Building…' : '↓ Summary PDF'}
              </button>
            </div>
          </div>
          <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ marginTop: 14, overflowX: 'auto' }}><PipelineStepper stage={pipelineStage(r)} size="lg" isDark={isDark} /></div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'grid', gap: 18, alignContent: 'start' }}>
        <div>
          {sectionTitle('Screening scan')}
          <div style={{ padding: 14, borderRadius: 8, background: surface2 }}>
            {!scan ? (
              <div style={{ fontSize: 12.5, color: muted }}>
                Not screened yet. A screening scan checks {r.domain} on its own and doesn't touch any existing vendor.
              </div>
            ) : scan.status === 'processing' ? (
              <div style={{ fontSize: 12.5, color: muted }}>
                Scanning {r.domain}… this page updates when it finishes.{' '}
                <Link to={`/scans/${scan.scan_id}/progress`} style={{ color: isDark ? '#60A5FA' : T.blue }}>Watch progress</Link>
              </div>
            ) : scan.status === 'failed' ? (
              <div style={{ fontSize: 12.5, color: danger }}>The last screening scan didn't finish. Run it again.</div>
            ) : scan.status === 'unavailable' ? (
              <div style={{ fontSize: 12.5, color: muted }}>A screening scan was run, but its results are no longer available.</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: TIER_COLOR[(scan.risk_level || 'Low') as Tier] || ink, lineHeight: 1 }}>{scan.overall_score}</span>
                    <span style={{ fontSize: 12, color: muted }}>/ 100 · {scan.risk_level} risk</span>
                  </div>
                  {scan.completed_at && <div style={{ fontSize: 11.5, color: muted, marginTop: 4 }}>Scanned {fmtDate(scan.completed_at)}</div>}
                </div>
                <Link to={`/scans/${scan.scan_id}/results`} style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#60A5FA' : T.blue, textDecoration: 'none', whiteSpace: 'nowrap' }}>Full report ›</Link>
              </div>
            )}
            {canAct && reviewing && scan?.status !== 'processing' && (
              <button style={{ ...topBtn(false, isDark), marginTop: 12, opacity: scanBusy ? 0.7 : 1 }} onClick={runScan} disabled={scanBusy}>
                {scanBusy ? 'Starting…' : scan ? 'Re-run screening scan' : 'Run screening scan'}
              </button>
            )}
          </div>
        </div>

        {(r.insight_summary || !!r.insight_top_findings?.length) && (
          <div>
            {sectionTitle('Security insight')}
            <div style={{ padding: 14, borderRadius: 8, background: surface2, display: 'grid', gap: 10 }}>
              {r.insight_summary && <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.55 }}>{r.insight_summary}</div>}
              {!!r.insight_top_findings?.length && (
                <div style={{ display: 'grid', gap: 6 }}>
                  {r.insight_top_findings.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <Pill label={f.severity} color={TIER_COLOR[f.severity as Tier] || T.slate} isDark={isDark} />
                        <span style={{ color: ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      </span>
                      {typeof f.score_gain_if_fixed === 'number' && f.score_gain_if_fixed > 0 && (
                        <span style={{ flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: isDark ? '#4ADE80' : T.grn }}>+{f.score_gain_if_fixed} pts if fixed</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 10.5, color: muted }}>
                Drafted automatically once the screening scan finished -- the point gains are the real scoring formula re-run with each finding fixed, never an estimate.
              </div>
            </div>
          </div>
        )}

        <RequestDocuments requestId={r.id} isDark={isDark} canAct={canAct} />

        <div>
          {sectionTitle('Request')}
          <RequestLinkedIn request={r} isDark={isDark} />
          {row('Contact', r.contact_name)}
          {row('Contact email', r.contact_email)}
          {row('Contact phone', r.contact_phone)}
          {row('Data sensitivity', SENSITIVITY[r.data_sensitivity] || r.data_sensitivity)}
          {row('Inherent tier', `${r.risk_tier} (from data sensitivity)`)}
          {row('Estimated spend', r.estimated_spend)}
          {row('Requested by', r.requested_by_name)}
          {row('Requested on', fmtDate(r.created_at))}
          {reviewing && wait !== null && row('Waiting for review', <span style={{ color: wait > STALE_WAIT_DAYS ? amber : ink }}>{waitLabel(wait)}</span>)}
          {r.reason && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12.5, color: muted, marginBottom: 4 }}>Why it's needed</div>
              <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.55 }}>{r.reason}</div>
            </div>
          )}
        </div>

        {(r.ai_research_summary || r.ai_suggested_data_sensitivity) && (
          <div>
            {sectionTitle('Research assist')}
            <div style={{ padding: 14, borderRadius: 8, background: surface2, display: 'grid', gap: 10 }}>
              {r.ai_research_summary && (
                <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.55 }}>{r.ai_research_summary}</div>
              )}
              {!!r.ai_research_sources?.length && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {r.ai_research_sources.map((src, i) => (
                    <a key={i} href={src.url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: isDark ? '#60A5FA' : T.blue, textDecoration: 'none' }}>{src.label} ↗</a>
                  ))}
                </div>
              )}
              {r.ai_suggested_data_sensitivity && (
                <div style={{ paddingTop: 8, borderTop: `1px solid ${line}` }}>
                  <div style={{ fontSize: 11.5, color: muted, marginBottom: 4 }}>Suggested data sensitivity</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Pill label={SENSITIVITY[r.ai_suggested_data_sensitivity] || r.ai_suggested_data_sensitivity} color={T.blue} isDark={isDark} />
                    {r.ai_suggested_data_sensitivity !== r.data_sensitivity && canAct && reviewing && (
                      <button
                        style={{ ...topBtn(false, isDark), padding: '4px 10px', fontSize: 11.5, opacity: sensitivityMutation.isPending ? 0.7 : 1 }}
                        disabled={sensitivityMutation.isPending}
                        onClick={() => sensitivityMutation.mutate(r.ai_suggested_data_sensitivity as string, {
                          onError: err => setError(apiError(err, 'Could not apply the suggestion.')),
                        })}
                      >
                        {sensitivityMutation.isPending ? 'Applying…' : `Apply (currently ${SENSITIVITY[r.data_sensitivity] || r.data_sensitivity})`}
                      </button>
                    )}
                  </div>
                  {r.ai_research_rationale && (
                    <div style={{ fontSize: 12, color: muted, marginTop: 6, lineHeight: 1.5 }}>{r.ai_research_rationale}</div>
                  )}
                </div>
              )}
              <div style={{ fontSize: 10.5, color: muted }}>Drafted automatically from public sources when this request was created. Advisory only — review before relying on it.</div>
            </div>
          </div>
        )}

        <div>
          {sectionTitle('Decision')}
          {r.status === 'promoted' ? (
            <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.55 }}>
              {r.created_vendor_id
                ? 'Approved and added as a tracked vendor.'
                : 'Approved and added as a vendor, but that vendor has since been deleted. This request is kept as the record of the decision.'}
            </div>
          ) : r.status === 'approved' ? (
            <div style={{ fontSize: 12.5, color: ink, lineHeight: 1.55 }}>
              Approved{r.decided_at ? ` on ${fmtDate(r.decided_at)}` : ''}. It's waiting in the Onboarding requests tab on the Vendors page, where it's added as a vendor and the rest of its details are filled in.
            </div>
          ) : r.status === 'rejected' ? (
            <div style={{ fontSize: 12.5, color: ink }}>Rejected{r.decided_at ? ` on ${fmtDate(r.decided_at)}` : ''}.</div>
          ) : canAct ? (
            <>
              {r.status === 'needs_info' && (
                <div style={{ fontSize: 12, color: STATUS.needs_info.color, marginBottom: 8 }}>
                  Marked as needing more information{r.decided_at ? ` on ${fmtDate(r.decided_at)}` : ''}.
                </div>
              )}
              {(!scan || scan.status !== 'completed') && (
                <div style={{ fontSize: 12, color: amber, marginBottom: 8 }}>No completed screening scan yet. You can still decide, but the score helps.</div>
              )}
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes for this decision (optional)" maxLength={2000}
                style={{
                  width: '100%', boxSizing: 'border-box', minHeight: 70, resize: 'vertical', padding: '8px 10px', fontSize: 12.5,
                  fontFamily: sans, color: ink, background: surface, border: `1px solid ${line}`, borderRadius: 6, outline: 'none',
                }} />
            </>
          ) : (
            <div style={{ fontSize: 12.5, color: muted }}>Waiting for an editor to review.</div>
          )}
          {r.reviewer_notes && !(canAct && reviewing) && (
            <div style={{ fontSize: 12.5, color: muted, marginTop: 8, fontStyle: 'italic' }}>"{r.reviewer_notes}"</div>
          )}
          {error && <div role="alert" style={{ fontSize: 12, color: danger, marginTop: 8 }}>{error}</div>}
        </div>
      </div>

      {(canAct || canDelete) && (
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${line}`, display: 'grid', gap: 8 }}>
          {canAct && reviewing && (
            <>
              <button style={{ ...topBtn(true, isDark), justifyContent: 'center', background: '#15803D', opacity: deciding ? 0.7 : 1 }}
                onClick={() => decide('approved')} disabled={!!deciding}>{deciding === 'approved' ? 'Saving…' : 'Approve'}</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...topBtn(false, isDark), flex: 1, justifyContent: 'center', opacity: deciding ? 0.7 : 1 }}
                  onClick={() => decide('needs_info')} disabled={!!deciding}>{deciding === 'needs_info' ? 'Saving…' : 'Needs more info'}</button>
                <button style={{ ...topBtn(false, isDark), flex: 1, justifyContent: 'center', color: danger, opacity: deciding ? 0.7 : 1 }}
                  onClick={() => decide('rejected')} disabled={!!deciding}>{deciding === 'rejected' ? 'Saving…' : 'Reject'}</button>
              </div>
            </>
          )}
          {canAct && r.status === 'approved' && (
            <button style={{ ...topBtn(true, isDark), justifyContent: 'center' }} onClick={onGoToOnboarding}>Go to Onboarding requests</button>
          )}
          {r.status === 'promoted' && r.created_vendor_id && (
            <Link to={`/vendors/${r.created_vendor_id}`} style={{ ...topBtn(true, isDark), justifyContent: 'center', textDecoration: 'none' }}>Open vendor</Link>
          )}
          {canDelete && r.status !== 'promoted' && (
            <button onClick={() => onDelete(r)} style={{
              ...topBtn(false, isDark), justifyContent: 'center', color: danger,
              borderColor: isDark ? '#7f1d1d' : T.redBdr, background: isDark ? '#1a0a0a' : T.redBg,
            }}>Delete request</button>
          )}
        </div>
      )}
    </aside>
  );
}

/* ─── Public documents (SEC filings / trust-center reports found for this request) ──────────── */

const DOC_TYPE_LABEL: Record<string, string> = { sec_filing: 'SEC filing', public_report: 'Public report' };

/* LinkedIn page row in the review drawer. Found automatically from the vendor's own website, never typed or edited. */
function RequestLinkedIn({ request: r, isDark }: { request: VendorRequest; isDark: boolean }) {
  const muted = isDark ? '#94a3b8' : T.muted;
  const accent = isDark ? '#60A5FA' : T.blue;
  const foundHost = (() => { try { return new URL(r.linkedin_found_on || '').hostname.replace(/^www\./, ''); } catch { return 'their website'; } })();
  return (
    <div style={{ fontSize: 12.5, padding: '6px 0', borderBottom: `1px solid ${isDark ? '#141414' : T.surface2}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <span style={{ color: muted }}>LinkedIn</span>
        {r.linkedin_url ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <LinkedInLink url={r.linkedin_url} size={14} />
            <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer"
              style={{ color: accent, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.linkedin_url.replace(/^https:\/\/www\./, '')}
            </a>
          </span>
        ) : <span style={{ color: muted }}>Not linked from their website</span>}
      </div>
      {r.linkedin_url && <div style={{ fontSize: 11, color: muted, textAlign: 'right', marginTop: 2 }}>Found on {foundHost}</div>}
    </div>
  );
}

function RequestDocuments({ requestId, isDark, canAct }: { requestId: string; isDark: boolean; canAct: boolean }) {
  const queryClient = useQueryClient();
  const ink = isDark ? '#e2e8f0' : T.ink;
  const muted = isDark ? '#94a3b8' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const key = ['vendor-requests', requestId, 'documents'];

  const docsQuery = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/v1/vendor-requests/${requestId}/documents`)).data.documents as RequestDocument[],
  });
  const detect = useMutation({
    mutationFn: async () => (await api.post(`/v1/vendor-requests/${requestId}/documents/detect`)).data as { documents: RequestDocument[] },
    onSuccess: data => queryClient.setQueryData(key, data.documents),
  });

  const docs = docsQuery.data ?? [];
  if (!docsQuery.isPending && docs.length === 0 && !detect.isPending && !detect.isSuccess) {
    // Nothing found yet (or never checked) -- show a quiet, collapsed state rather than an empty card.
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 11.5, color: muted }}>
        <span>No public documents (SEC filings, trust-center reports) found for this vendor yet.</span>
        {canAct && (
          <button style={{ background: 'none', border: 'none', padding: 0, color: isDark ? '#60A5FA' : T.blue, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap' }}
            disabled={detect.isPending} onClick={() => detect.mutate()}>
            {detect.isPending ? 'Checking…' : 'Check again'}
          </button>
        )}
      </div>
    );
  }
  if (docsQuery.isPending) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: ink }}>Public documents ({docs.length})</div>
        {canAct && (
          <button style={{ background: 'none', border: 'none', padding: 0, color: isDark ? '#60A5FA' : T.blue, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}
            disabled={detect.isPending} onClick={() => detect.mutate()}>
            {detect.isPending ? 'Checking…' : 'Check again'}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {docs.map(doc => (
          <a key={doc.id} href={doc.source_url} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 7, border: `1px solid ${line}`,
            textDecoration: 'none', fontSize: 12,
          }}>
            <span style={{ color: ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{doc.title}</span>
            <span style={{ flexShrink: 0, fontSize: 10.5, color: muted }}>{doc.document_type || DOC_TYPE_LABEL[doc.source_type] || doc.source_type}</span>
            <span style={{ flexShrink: 0, color: isDark ? '#60A5FA' : T.blue }}>↗</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ─── New request ──────────────────────────────────────────────────── */

function NewRequestModal({ isDark, onClose, onCreated }: {
  isDark: boolean; onClose: () => void; onCreated: (req: VendorRequest, scanError?: string) => void;
}) {
  const muted = isDark ? '#94a3b8' : T.muted;
  const ink = isDark ? '#e2e8f0' : T.ink;
  const line = isDark ? '#1f1f1f' : T.bdr;

  const [vendorName, setVendorName] = useState('');
  const [domain, setDomain] = useState('');
  const [reason, setReason] = useState('');
  const [dataSensitivity, setDataSensitivity] = useState('none');
  const [estimatedSpend, setEstimatedSpend] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // Starts at the workspace default (Settings -> Workflow defaults) unless already toggled here.
  const defaultsQuery = useQuery(settingsQueries.defaults());
  const [scanNowChoice, setScanNow] = useState<boolean | null>(null);
  const scanNow = scanNowChoice ?? defaultsQuery.data?.values.screen_on_request ?? true;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const cleanDomain = normalizeDomain(domain);
  // The real, possibly tenant-customized mapping (Settings -> Vendor Risk Tiering); TIER_BY_SENSITIVITY
  // below is only the fallback shown for the instant it takes to load.
  const tieringQuery = useQuery(settingsQueries.riskTiering());
  const sensitivityTiers = tieringQuery.data?.sensitivity_tiers ?? TIER_BY_SENSITIVITY;
  const tier = (sensitivityTiers[dataSensitivity] || 'Low') as Tier;

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: ink,
    background: isDark ? '#0d0d0d' : T.surface, border: `1px solid ${line}`, borderRadius: 6, outline: 'none',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: muted, marginBottom: 4 };

  const save = async () => {
    if (!vendorName.trim()) { setError('Enter a vendor name.'); return; }
    if (!cleanDomain) { setError('Enter a domain.'); return; }
    if (!contactName.trim()) { setError('Enter a contact name for this vendor.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail.trim())) { setError('Enter a valid contact email address.'); return; }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/v1/vendor-requests/', {
        vendor_name: vendorName.trim(), domain: cleanDomain, reason: reason.trim() || null,
        data_sensitivity: dataSensitivity, estimated_spend: estimatedSpend.trim() || null,
        contact_name: contactName.trim(), contact_email: contactEmail.trim(), contact_phone: contactPhone.trim() || null,
      });
      if (!scanNow) { onCreated(data); return; }
      try {
        await api.post(`/v1/vendor-requests/${data.id}/scan`);
        const refreshed = await api.get(`/v1/vendor-requests/${data.id}`);
        onCreated(refreshed.data);
      } catch (scanErr) {
        onCreated(data, apiError(scanErr, 'Request created, but the screening scan could not start.'));
      }
    } catch (err) {
      setError(apiError(err, 'Could not create the request. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="New vendor request" style={{
        width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto', background: isDark ? '#0d0d0d' : T.surface,
        border: `1px solid ${line}`, borderRadius: 10, padding: 20, fontFamily: sans,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: ink }}>Screen a new vendor</div>
        <div style={{ fontSize: 12, color: muted, marginTop: 2, marginBottom: 14 }}>Nothing is tracked or scored as a vendor until the request is approved and added.</div>
        <form noValidate onSubmit={e => { e.preventDefault(); if (!saving) save(); }} style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={labelStyle} htmlFor="vr-name">Vendor name</label>
            <input id="vr-name" style={inputStyle} value={vendorName} autoFocus maxLength={200} placeholder="Nimbus Data Co" onChange={e => setVendorName(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="vr-domain">Website or domain</label>
            <input id="vr-domain" style={inputStyle} value={domain} maxLength={255} placeholder="nimbusdata.io" onChange={e => setDomain(e.target.value)} />
            {domain.trim() && cleanDomain !== domain.trim() && (
              <div style={{ fontSize: 11.5, color: muted, marginTop: 4 }}>Will screen <strong style={{ color: ink }}>{cleanDomain || '—'}</strong></div>
            )}
          </div>
          <div>
            <label style={labelStyle} htmlFor="vr-reason">Why is this vendor needed?</label>
            <textarea id="vr-reason" style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} value={reason} maxLength={2000}
              placeholder="What will it be used for, and by which team?" onChange={e => setReason(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="vr-sensitivity">What data will it handle?</label>
            <select id="vr-sensitivity" style={inputStyle} value={dataSensitivity} onChange={e => setDataSensitivity(e.target.value)}>
              {Object.entries(SENSITIVITY).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <div style={{ fontSize: 11.5, color: muted, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              Inherent tier: <Pill label={tier} color={TIER_COLOR[tier]} isDark={isDark} />
            </div>
          </div>
          <div>
            <label style={labelStyle} htmlFor="vr-spend">Estimated spend (optional)</label>
            <input id="vr-spend" style={inputStyle} value={estimatedSpend} maxLength={100} placeholder="$40k/year" onChange={e => setEstimatedSpend(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gap: 10, padding: 12, borderRadius: 8, border: `1px solid ${line}` }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: muted }}>Contact at the vendor</div>
            <div>
              <label style={labelStyle} htmlFor="vr-contact-name">Name</label>
              <input id="vr-contact-name" style={inputStyle} value={contactName} maxLength={200} placeholder="Jane Doe" onChange={e => setContactName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle} htmlFor="vr-contact-email">Email</label>
                <input id="vr-contact-email" type="email" style={inputStyle} value={contactEmail} maxLength={254} placeholder="jane@nimbusdata.io" onChange={e => setContactEmail(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle} htmlFor="vr-contact-phone">Phone (optional)</label>
                <PhoneNumberInput id="vr-contact-phone" inputStyle={inputStyle} value={contactPhone} placeholder="555 0100" onChange={setContactPhone} />
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: ink, cursor: 'pointer' }}>
            <input type="checkbox" checked={scanNow} onChange={e => setScanNow(e.target.checked)} />
            Run a screening scan right away
          </label>
          {error && <div style={{ fontSize: 12, color: isDark ? '#F87171' : T.red }} role="alert">{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" style={topBtn(false, isDark)} onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" style={{ ...topBtn(true, isDark), opacity: saving ? 0.7 : 1 }} disabled={saving}>
              {saving ? 'Creating…' : 'Create request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Bulk upload: upload -> review (every extracted field) -> confirm ─────────────────────── */

interface BulkRowResult {
  row: number;
  vendor_name: string | null;
  domain: string | null;
  status: 'created' | 'skipped';
  detail?: string;
}

interface BulkRequestPreviewRow {
  row: number;
  status: 'ready' | 'skipped';
  detail: string | null;
  vendor_name: string; domain: string; reason: string | null; data_sensitivity: string;
  estimated_spend: string | null; contact_name: string | null; contact_email: string | null;
  contact_phone: string | null; risk_tier: Tier;
}

function BulkUploadModal({ isDark, onClose, onDone }: { isDark: boolean; onClose: () => void; onDone: (created: number) => void }) {
  const muted = isDark ? '#94a3b8' : T.muted;
  const ink = isDark ? '#e2e8f0' : T.ink;
  const line = isDark ? '#1f1f1f' : T.bdr;
  const red = isDark ? '#F87171' : T.red;

  const [step, setStep] = useState<'upload' | 'review' | 'done'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ ready: number; skipped: number; rows: BulkRequestPreviewRow[] } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<{ created: number; skipped: number; results: BulkRowResult[] } | null>(null);

  const downloadTemplate = async () => {
    // window.open() can't attach the auth header, so fetch through the api client and save as a blob.
    try {
      const { data } = await api.get('/v1/vendor-requests/bulk/template', { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pre_onboarding_requests_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not download the template. Try again.');
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/v1/vendor-requests/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(data);
      setSelected(new Set(data.rows.filter((r: BulkRequestPreviewRow) => r.status === 'ready').map((r: BulkRequestPreviewRow) => r.row)));
      setStep('review');
    } catch (err) {
      setError(apiError(err, 'Upload failed. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (row: number) => setSelected(prev => {
    const next = new Set(prev);
    next.has(row) ? next.delete(row) : next.add(row);
    return next;
  });

  const confirm = async () => {
    if (!preview) return;
    const rows = preview.rows.filter(r => r.status === 'ready' && selected.has(r.row));
    if (!rows.length) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/v1/vendor-requests/bulk/confirm', { rows });
      setResult(data);
      setStep('done');
    } catch (err) {
      setError(apiError(err, 'Could not add these requests. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, value: React.ReactNode) => value ? (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 12, color: ink, marginTop: 1, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Bulk upload vendor requests" style={{
        width: '100%', maxWidth: step === 'review' ? 720 : 520, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        background: isDark ? '#0d0d0d' : T.surface, border: `1px solid ${line}`, borderRadius: 10, padding: 20, fontFamily: sans,
      }}>
        {step === 'upload' && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 6 }}>Bulk upload requests</div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 14, lineHeight: 1.55 }}>
              A CSV with columns <code>vendor_name, domain, reason, data_sensitivity, estimated_spend, contact_name, contact_email,
              contact_phone</code>, one row per vendor (contact columns optional here). You'll review every request extracted
              from the file -- nothing is added until you confirm.{' '}
              <button onClick={downloadTemplate} style={{ ...linkBtn(isDark), fontSize: 12, padding: 0 }}>Download template</button>
            </div>
            <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)} style={{ fontSize: 12.5, color: ink, marginBottom: 14 }} />
            {error && <div style={{ fontSize: 12, color: red, marginBottom: 12 }} role="alert">{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" style={topBtn(false, isDark)} onClick={onClose} disabled={busy}>Cancel</button>
              <button type="button" style={{ ...topBtn(true, isDark), opacity: (!file || busy) ? 0.6 : 1 }} onClick={upload} disabled={!file || busy}>
                {busy ? 'Reading file…' : 'Upload & review'}
              </button>
            </div>
          </>
        )}

        {step === 'review' && preview && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 2 }}>Review before adding</div>
            <div style={{ fontSize: 12, color: muted, marginBottom: 12 }}>
              Extracted from your file -- check what will actually be requested. <strong style={{ color: ink }}>{selected.size}</strong> of{' '}
              {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'} selected
              {preview.skipped > 0 && <span> &middot; {preview.skipped} can't be imported (see why below)</span>}.
            </div>
            <div style={{ flex: 1, overflow: 'auto', display: 'grid', gap: 8, marginBottom: 14, paddingRight: 2 }}>
              {preview.rows.map(r => {
                const ready = r.status === 'ready';
                const checked = ready && selected.has(r.row);
                const details = [
                  field('Reason', r.reason),
                  field('Data sensitivity', SENSITIVITY[r.data_sensitivity] || r.data_sensitivity),
                  field('Inherent tier', ready ? <Pill label={r.risk_tier} color={TIER_COLOR[r.risk_tier]} isDark={isDark} /> : null),
                  field('Estimated spend', r.estimated_spend),
                  field('Contact', [r.contact_name, r.contact_email].filter(Boolean).join(' · ') || r.contact_phone),
                ].filter(Boolean);
                return (
                  <div key={r.row} style={{
                    display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${ready ? line : (isDark ? '#3f1d1d' : '#FCA5A5')}`,
                    background: ready ? 'transparent' : (isDark ? 'rgba(248,113,113,.06)' : '#FEF2F2'), opacity: ready ? 1 : 0.85,
                  }}>
                    <input type="checkbox" checked={checked} disabled={!ready} onChange={() => toggleRow(r.row)} style={{ marginTop: 3, flexShrink: 0 }} />
                    <VendorLogo name={r.vendor_name || '?'} domain={r.domain} size={32} isDark={isDark} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: ink }}>{r.vendor_name || `Row ${r.row}`}</span>
                        <span style={{ fontSize: 11.5, color: muted }}>{r.domain}</span>
                        {!ready && <span style={{ fontSize: 10.5, fontWeight: 700, color: red }}>{r.detail}</span>}
                      </div>
                      {details.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '6px 12px', marginTop: 8 }}>
                          {details}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {error && <div style={{ fontSize: 12, color: red, marginBottom: 10 }} role="alert">{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <button type="button" style={topBtn(false, isDark)} onClick={() => { setStep('upload'); setPreview(null); setFile(null); }} disabled={busy}>
                ← Choose a different file
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" style={topBtn(false, isDark)} onClick={onClose} disabled={busy}>Cancel</button>
                <button type="button" style={{ ...topBtn(true, isDark), opacity: (!selected.size || busy) ? 0.6 : 1 }} onClick={confirm} disabled={!selected.size || busy}>
                  {busy ? 'Adding…' : `Add ${selected.size} request${selected.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'done' && result && (
          <>
            <div style={{ fontSize: 15, fontWeight: 700, color: ink, marginBottom: 6 }}>Bulk upload requests</div>
            <div style={{ fontSize: 12.5, color: ink, marginBottom: 12 }}>
              <strong>{result.created}</strong> created, <strong>{result.skipped}</strong> skipped.
            </div>
            <div style={{ border: `1px solid ${line}`, borderRadius: 6, marginBottom: 14, maxHeight: 300, overflow: 'auto' }}>
              {result.results.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', fontSize: 12, borderTop: i === 0 ? 'none' : `1px solid ${line}` }}>
                  <span style={{ flexShrink: 0, width: 60, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', color: r.status === 'created' ? (isDark ? '#34d399' : T.grn) : muted }}>{r.status}</span>
                  <span style={{ color: ink, flexShrink: 0 }}>{r.vendor_name || `Row ${r.row}`}</span>
                  {r.detail && <span style={{ color: muted }}>· {r.detail}</span>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" style={topBtn(true, isDark)} onClick={() => onDone(result.created)}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
